/**
 * QR分解を用いた数値的に安定な行列計算
 * 
 * 正規方程式の直接的な逆行列計算の代わりに、
 * QR分解を使用することで数値的安定性を向上させます。
 */

import { Matrix } from './matrix-utils';

/**
 * QR分解
 * A = QR where Q is orthogonal and R is upper triangular
 * 
 * Modified Gram-Schmidt法を使用（数値的により安定）
 */
export function qrDecomposition(A: Matrix): { Q: Matrix, R: Matrix } {
  const m = A.rows;
  const n = A.cols;
  const Q = new Matrix(m, n);
  const R = new Matrix(n, n);
  
  // Modified Gram-Schmidt process
  for (let j = 0; j < n; j++) {
    // 列jを取得
    const v = new Matrix(m, 1);
    for (let i = 0; i < m; i++) {
      v.set(i, 0, A.get(i, j));
    }
    
    // 直交化
    for (let i = 0; i < j; i++) {
      let dotProduct = 0;
      for (let k = 0; k < m; k++) {
        dotProduct += Q.get(k, i) * v.get(k, 0);
      }
      R.set(i, j, dotProduct);
      
      // v = v - R[i,j] * Q[:,i]
      for (let k = 0; k < m; k++) {
        v.set(k, 0, v.get(k, 0) - dotProduct * Q.get(k, i));
      }
    }
    
    // ノルムを計算
    let norm = 0;
    for (let i = 0; i < m; i++) {
      norm += v.get(i, 0) * v.get(i, 0);
    }
    norm = Math.sqrt(norm);
    
    // 数値的な安定性のチェック
    if (norm < 1e-10) {
      throw new Error('Matrix is rank deficient');
    }
    
    R.set(j, j, norm);
    
    // 正規化してQに格納
    for (let i = 0; i < m; i++) {
      Q.set(i, j, v.get(i, 0) / norm);
    }
  }
  
  return { Q, R };
}

/**
 * QR分解を使用した最小二乗解
 * Ax = b を解く
 * 
 * A = QR とすると、x = R^(-1) * Q' * b
 */
export function solveByQR(A: Matrix, b: Matrix): Matrix {
  const { Q, R } = qrDecomposition(A);
  
  // Q' * b を計算
  const Qtb = Q.transpose().multiply(b);
  
  // 後退代入でRx = Q'bを解く
  const n = R.cols;
  const x = new Matrix(n, 1);
  
  for (let i = n - 1; i >= 0; i--) {
    let sum = Qtb.get(i, 0);
    
    for (let j = i + 1; j < n; j++) {
      sum -= R.get(i, j) * x.get(j, 0);
    }
    
    const Rii = R.get(i, i);
    if (Math.abs(Rii) < 1e-10) {
      throw new Error('Matrix is singular');
    }
    
    x.set(i, 0, sum / Rii);
  }
  
  return x;
}

/**
 * 条件数の推定
 * 行列の条件数が大きい場合は数値的に不安定
 */
export function estimateConditionNumber(A: Matrix): number {
  // 簡易的な推定: ||A|| * ||A^(-1)||の近似
  // より正確にはSVDを使用すべきだが、計算コストを考慮
  
  let maxNorm = 0;
  let minNorm = Infinity;
  
  // 列ノルムの最大値と最小値を計算
  for (let j = 0; j < A.cols; j++) {
    let colNorm = 0;
    for (let i = 0; i < A.rows; i++) {
      colNorm += A.get(i, j) * A.get(i, j);
    }
    colNorm = Math.sqrt(colNorm);
    
    maxNorm = Math.max(maxNorm, colNorm);
    minNorm = Math.min(minNorm, colNorm);
  }
  
  if (minNorm < 1e-10) {
    return Infinity;
  }
  
  return maxNorm / minNorm;
}

/**
 * データの標準化
 * 各列を平均0、標準偏差1に変換
 */
export function standardizeData(X: number[][], includeIntercept: boolean): {
  X_std: number[][],
  means: number[],
  stds: number[]
} {
  const n = X.length;
  const p = X[0].length;
  const means: number[] = new Array(p).fill(0);
  const stds: number[] = new Array(p).fill(0);
  
  // 平均を計算
  for (let j = 0; j < p; j++) {
    let sum = 0;
    for (let i = 0; i < n; i++) {
      sum += X[i][j];
    }
    means[j] = sum / n;
  }
  
  // 標準偏差を計算
  for (let j = 0; j < p; j++) {
    let sumSq = 0;
    for (let i = 0; i < n; i++) {
      const diff = X[i][j] - means[j];
      sumSq += diff * diff;
    }
    stds[j] = Math.sqrt(sumSq / (n - 1));
    
    // 標準偏差が0の場合（定数列）は1に設定
    if (stds[j] < 1e-10) {
      stds[j] = 1;
    }
  }
  
  // 標準化
  const X_std: number[][] = [];
  for (let i = 0; i < n; i++) {
    const row: number[] = [];
    if (includeIntercept) {
      row.push(1); // 切片項は標準化しない
    }
    for (let j = 0; j < p; j++) {
      row.push((X[i][j] - means[j]) / stds[j]);
    }
    X_std.push(row);
  }
  
  return { X_std, means, stds };
}

/**
 * 係数を元のスケールに戻す
 */
export function unstandardizeCoefficients(
  coefficients: number[],
  means: number[],
  stds: number[],
  yMean: number,
  yStd: number,
  includeIntercept: boolean
): number[] {
  const result: number[] = [];
  
  if (includeIntercept) {
    // 切片の調整
    let intercept = coefficients[0] * yStd + yMean;
    for (let j = 0; j < means.length; j++) {
      intercept -= coefficients[j + 1] * yStd * means[j] / stds[j];
    }
    result.push(intercept);
    
    // 他の係数
    for (let j = 0; j < means.length; j++) {
      result.push(coefficients[j + 1] * yStd / stds[j]);
    }
  } else {
    // 切片なしの場合
    for (let j = 0; j < means.length; j++) {
      result.push(coefficients[j] * yStd / stds[j]);
    }
  }
  
  return result;
}